// HexDetail: the hover-card facts of one hex, each traced to the sources that produced it.
//
//   grid fields ──▶ names (terrain, feature, hill, river, resource) ─┐
//   snapshot ─────▶ owner, controller, control, city, wonder         ├─▶ HexDetail + sources
//   sources ──────▶ physical bake · borders · control · site records ┘
//
// `sources` lists only what backs a field the card shows: a plain grassland hex cites the
// elevation and land-cover sources, not the ice ones.

import { FEATURE, HILL, RESOURCE, TERRAIN, type Deposit, type DateStr, type SourceId } from '../core/schema.ts';
import { CONTROL, type HexDetail, type WorldSnapshot } from './api.ts';
import type { ActorTable } from './actors.ts';
import { BASIS, controlAt, controlSourcesAt, extentOn, gapsAt, type ControlModel, type Extent } from './control.ts';
import { defaultOwners, ownerSources, type OwnerModel, type PovKey } from './owners.ts';
import type { Hemisphere } from './seaice.ts';
import { uniq } from './util.ts';

/** Source ids of the physical bake (docs/bake/physical.md). */
const ETOPO: SourceId = 'phys:etopo2022';
const NE_LAND: SourceId = 'phys:ne-land';
const NE_LAKES: SourceId = 'phys:ne-lakes';
const NE_RIVERS: SourceId = 'phys:ne-rivers';
const NE_GLACIERS: SourceId = 'phys:ne-glaciers';
const NE_ICE_SHELVES: SourceId = 'phys:ne-ice-shelves';
const KOPPEN: SourceId = 'phys:koppen-beck2023';
const WORLDCOVER: SourceId = 'phys:worldcover2021';
const NSIDC_INDEX: SourceId = 'phys:nsidc-g02135-v4';

const RIVER_NAMES: Array<HexDetail['river']> = ['none', 'river', 'major'];

const CONTROL_LABEL: Record<number, string | null> = {
  [CONTROL.NONE]: null,
  [CONTROL.OCCUPIED]: 'occupied',
  [CONTROL.CONTESTED]: 'contested',
  [CONTROL.ANNEXATION_CLAIMED]: 'annexation claimed',
  [CONTROL.BREAKAWAY]: 'breakaway',
};

/**
 * How the held share of a hex is known: a source says so, the map's convention (half of a partly held unit),
 * a reported share of the whole unit ('extent', see HexDetailHeld.controlExtent), or nobody knows (a declared gap).
 */
export type ControlBasisName = 'recorded' | 'convention' | 'unknown' | 'extent';

const BASIS_NAME: Record<number, ControlBasisName> = {
  [BASIS.RECORDED]: 'recorded',
  [BASIS.CONVENTION]: 'convention',
  [BASIS.UNKNOWN]: 'unknown',
  [BASIS.EXTENT]: 'extent',
};

/** Per-hex arrays the card reads (all from the physical and sites grid files). */
export interface HexFields {
  lat: Float32Array;
  lon: Float32Array;
  elev: Int16Array;
  terrain: Uint8Array;
  feature: Uint8Array;
  hill: Uint8Array;
  riverSize: Uint8Array;
  city: Uint16Array;
  wonder: Uint16Array;
  resource: Uint8Array;
}

export interface HexInputs {
  fields: HexFields;
  actors: ActorTable;
  owners: OwnerModel;
  control: ControlModel;
  /** Deposits grouped by their hex. */
  deposits: Map<number, Deposit[]>;
  /** The Natural Earth admin-1 code of the unit a hex is in, or null. */
  unitCode: (hex: number) => string | null;
  /** LAKE hexes that cite more than the lakes layer (the Caspian, promoted hexes), by hex (lake-hexes.json). */
  lakeSources: ReadonlyMap<number, SourceId[]>;
  /** The sea-ice files behind a hemisphere's ice at `date`, when the world has sea-ice data. */
  iceSources: (date: DateStr, side: Hemisphere) => SourceId[];
}

/** 'RARE_EARTHS' → 'Rare earths'. */
function label(key: string): string {
  const s = key.toLowerCase().replace(/_/g, ' ');
  return s[0].toUpperCase() + s.slice(1);
}

function nameOf(table: Record<string, number>, value: number): string | null {
  const key = Object.keys(table).find((k) => table[k] === value);
  return key ? label(key) : null;
}

function terrainSources(terrain: number): SourceId[] {
  switch (terrain) {
    case TERRAIN.OCEAN:
    case TERRAIN.COAST:
      return [ETOPO, NE_LAND];
    case TERRAIN.LAKE:
      return [NE_LAKES];
    case TERRAIN.SNOW:
      return [KOPPEN, NE_LAND, NE_GLACIERS];
    default:
      return [KOPPEN, NE_LAND];
  }
}

function featureSources(feature: number): SourceId[] {
  switch (feature) {
    case FEATURE.WOODS:
    case FEATURE.MARSH:
      return [WORLDCOVER];
    case FEATURE.RAINFOREST:
      return [WORLDCOVER, KOPPEN];
    case FEATURE.FLOODPLAINS:
      return [KOPPEN, NE_RIVERS];
    case FEATURE.ICE:
      // Antarctic ice shelves only: sea ice is not in the grid, its citations come from seaIce below.
      return [NE_GLACIERS, NE_ICE_SHELVES];
    default:
      return [];
  }
}

/** The controller's display name, or null when it is the owner (the snapshot leaves it unset then). */
function actorName(t: ActorTable, index: number): string | null {
  return index === 0 ? null : (t.list[index]?.name ?? null);
}

/**
 * HexDetail plus the share of the hex the controller holds (0..255) and how that is known; to be folded
 * into HexDetail. With basis 'convention' the share is drawn as half, not measured.
 */
export type HexDetailHeld = HexDetail & { controlFrac: number; controlBasis: ControlBasisName; controlExtent: Extent | null };

/** The snapshot the card reads: WorldSnapshot with the basis of its control arrays. */
type HeldSnapshot = WorldSnapshot & { controlBasis: Uint8Array };

/** "unknown · no observation until 2022-04-03": what the card says for a hex inside a declared gap. */
function unknownLabel(inp: HexInputs, date: DateStr): string {
  const gap = gapsAt(inp.control, date)[0];
  return gap ? `unknown · no observation until ${gap.to}` : 'unknown';
}

/** `pov` is null for a state view (sandbox): the owner's sources then come from the default worldview. */
export function hexDetail(inp: HexInputs, hex: number, date: DateStr, snap: HeldSnapshot, pov: PovKey | null): HexDetailHeld {
  const f = inp.fields;
  const lat = f.lat[hex];
  const sources: SourceId[] = [];

  const terrain = nameOf(TERRAIN, f.terrain[hex]) ?? 'Unknown';
  sources.push(...(inp.lakeSources.get(hex) ?? terrainSources(f.terrain[hex])), ETOPO);

  const feature = f.feature[hex] === FEATURE.NONE ? null : nameOf(FEATURE, f.feature[hex]);
  sources.push(...featureSources(f.feature[hex]));

  const hill = f.hill[hex] === HILL.FLAT ? null : nameOf(HILL, f.hill[hex]);
  const river = RIVER_NAMES[f.riverSize[hex]] ?? 'none';
  if (river !== 'none') {
    sources.push(NE_RIVERS);
  }

  // A hex the leader's point of view re-assigns is a claim, not ownership (ARCHITECTURE §3.3): the
  // card names the default-worldview owner and who holds the hex there, then adds "claimed by …".
  //
  //   RUS view, Crimea:  owner Ukraine · held by Russia · "claimed by Russia"
  const claimant = snap.owner[hex];
  const defOwner = defaultOwners(inp.owners, date);
  const claimed = claimant !== 0 && claimant !== defOwner[hex];
  const held = claimed ? controlAt(inp.control, date, defOwner, defOwner) : snap;

  const owner = actorName(inp.actors, claimed ? defOwner[hex] : claimant);
  if (claimant !== 0) {
    sources.push(...ownerSources(inp.owners, date, pov, hex, inp.unitCode(hex)));
  }

  const ctrlIndex = held.controller[hex];
  const controller = actorName(inp.actors, ctrlIndex);
  if (ctrlIndex !== 0) {
    sources.push(...controlSourcesAt(inp.control, date, hex, ctrlIndex));
  }
  const claim = claimed ? `claimed by ${actorName(inp.actors, claimant)}` : null;
  const basis = BASIS_NAME[held.controlBasis[hex]] ?? 'recorded';
  const how = basis === 'unknown' ? unknownLabel(inp, date) : (CONTROL_LABEL[held.control[hex]] ?? null);
  const control = [how, claim].filter(Boolean).join(' · ') || null;

  // Ice on this date's month (seaice.bin): the index that defines the concentration and the month's files.
  if ((snap.seaIce?.[hex] ?? 0) > 0) {
    sources.push(NSIDC_INDEX, ...inp.iceSources(date, lat < 0 ? 'south' : 'north'));
  }

  const cityId = f.city[hex];
  const city = cityId === 0 ? null : (snap.cities.find((c) => c.id === cityId) ?? null);
  const wonderId = f.wonder[hex];
  const wonder = wonderId === 0 ? null : (snap.wonders.find((w) => w.id === wonderId) ?? null);
  sources.push(...(city?.sources ?? []), ...(wonder?.sources ?? []));

  const resourceKind = f.resource[hex];
  const resource = resourceKind === RESOURCE.NONE ? null : nameOf(RESOURCE, resourceKind);
  for (const d of inp.deposits.get(hex) ?? []) {
    sources.push(...d.sources);
  }

  return {
    hex,
    lat,
    lon: f.lon[hex],
    terrain,
    feature,
    hill,
    elevM: f.elev[hex],
    river,
    owner,
    controller,
    control,
    controlFrac: held.controlFrac[hex],
    controlBasis: basis,
    controlExtent: basis === 'extent' ? extentOn(inp.control, date, hex, ctrlIndex) : null,
    city,
    wonder,
    resource,
    sources: uniq(sources),
  };
}
